import debug from 'webmd-debug';

/* eslint-disable import/prefer-default-export */
export const videoObserverMixin = {
	methods: {
		/**
		 * observeVideos - Sets up an IntersectionObserver
		 * to monitor the visibility of the video element
		 * and trigger specific actions (like autoplay and impression tracking) based on the video's
		 * intersection with the viewport.
		 *
		 * When the video comes into view:
		 * 	- Calls the 'updateInviewAutoPlay' method
		 * 	to handle autoplay behavior if 'inviewAutoplay' is enabled in the configuration.
		 * 	- Calls the 'trackVideoImpression' method
		 * 	to track video impressions for metrics and stop further observation once tracked.
		 *
		 * Observer options:
		 * - threshold: 0.5 (i.e., the callback is triggered when 50% of the video is visible).
		 */
		observeVideos() {
			const $vm = this;
			const video = document.getElementById($vm.video.id);
			if (!video) return;

			debug('[vueVideoPlayer] Observe Video', $vm.video.id);

			/**
			 * Initializes an IntersectionObserver to monitor the visibility of the video element
			 */
			const observer = new IntersectionObserver((entries) => {
				const entry = entries[0];

				// Handle Inview Autoplay if enabled in the configuration
				if ($vm.config.inviewAutoplay) {
					$vm.updateInviewAutoPlay(entry);
				}

				// Track video impression metrics and stop observing if already tracked
				if ($vm.mode !== 'symphony') {
					$vm.trackVideoImpression(entry, observer, video);
				}
			},
			{
				threshold: 0.5,
			});
			observer.observe(video);
		},

		/**
		 * Handles "Autoplay in View" functionality.
		 * This method is triggered when an intersection observer detects
		 * a video element entering or exiting the viewport.
		 *
		 * @param {IntersectionObserverEntry} entry - The entry object
		 * representing the video element's intersection state.
		 */
		updateInviewAutoPlay(entry) {
			const $vm = this;

			const vidID = entry.target.dataset.dynvidid;
			debug('[vueVideoPlayer] inview autoplay', entry);

			if (entry.isIntersecting) {
				window.isInviewMap.set(vidID, true);
				$vm.config.dynVidId = vidID;

				// Check if user manually playing other video
				const userPlayingOtherVideo = Object.entries(window.dynVideoState).find(([key, value]) => value.userState === 'play' && vidID !== key);

				/**
				 * This 'if' block executes whenever nowPlaying is ''
				 * And is also needed when user has not manually paused the video
				 * We check userState and nowPlaying in combination
				 * and determine if the video in view should play or no
				 */
				if (
					!userPlayingOtherVideo
					&& window.dynVideoState[vidID].userState !== 'pause'
					&& window.nowPlaying === ''
					&& $vm.ampPlayer.appState.playState !== 'playing'
				) {
					if (window.dynVideoState[vidID].audioState !== 'unmuted') {
						$vm.ampPlayer.autoplay = 'muted';
					}
					debug('[vueVideoPlayer] should Play', vidID);
					$vm.ampPlayer.play().catch(() => {
						debug('[vueVideoPlayer] autoplay threshold', $vm.ampPlayer.autoplayThreshold);
						if ($vm.ampPlayer.autoplayThreshold === 'muted') {
							$vm.ampPlayer.muted = true;
							$vm.ampPlayer.play();
						}
					});
					window.nowPlaying = vidID;
				}
			} else {
				window.isInviewMap.set(vidID, false);

				/**
				 * This 'if' block will pause the video that is out of view
				 * and play the video that is currently in view
				 */
				if (window.nowPlaying === vidID) {
					debug('[vueVideoPlayer] should pause', vidID);
					$vm.ampPlayer.pause();

					/*
					 * Find the videos in view that are not paused
					 * Check if there are any videos in view that are not paused &
					 * Set the 'nowPlaying' video based on scroll direction
					 * else set 'nowPlaying' to empty.
					 */
					let nowPlayingVideo = '';
					const shouldPlayArr = Object.keys(window.dynVideoState)
						.filter((id) => window.isInviewMap.get(id) === true
							&& window.dynVideoState[id].userState !== 'pause');

					// Check if user manually playing any video
					const userPlayingVideo = Object.entries(window.dynVideoState).find(([, value]) => value.userState === 'play');

					if (shouldPlayArr.length !== 0 && !userPlayingVideo) {
						nowPlayingVideo = this.scrollDirection === 'down'
							? shouldPlayArr[0] : shouldPlayArr[shouldPlayArr.length - 1];
					}
					window.nowPlaying = nowPlayingVideo;

					/**
					 * We use userState to check if any video that is in view is manually paused
					 * If it is manually paused, then it should not resume playing
					 * else video should start or continue to play
					 */
					if (window.nowPlaying !== '') {
						if (window.dynVideoState[window.nowPlaying].audioState !== 'unmuted') {
							window.vueVideo[window.nowPlaying].autoplay = 'muted';
						}
						debug('[vueVideoPlayer] should play', window.nowPlaying);
						window.vueVideo[window.nowPlaying].play().catch(() => {
							debug('[vueVideoPlayer] autoplay threshold', $vm.ampPlayer.autoplayThreshold);
							if (window.vueVideo[window.nowPlaying].autoplayThreshold === 'muted') {
								window.vueVideo[window.nowPlaying].muted = true;
								window.vueVideo[window.nowPlaying].play();
							}
						});
					}
				}

				// close About/transcript if video is out of view
				if ($vm.$refs.infobar.isAboutOpen || $vm.$refs.infobar.isTranscriptOpen) {
					$vm.$refs.infobar.isAboutOpen = false;
					$vm.$refs.infobar.isTranscriptOpen = false;
				}
			}
		},

		/**
		 * trackVideoImpression:
		 * This method tracks whether a video has been viewed (impression) and marks it as tracked.
		 * Once the impression is recorded, it optionally stops observing the video
		 * unless inview autoplay is enabled.
		 *
		 * @param {IntersectionObserverEntry} entry - The observer entry
		 * containing information about the video element.
		 * @param {IntersectionObserver} observer - The IntersectionObserver instance
		 * observing the video.
		 * @param {HTMLElement} video - The video element being observed.
		 */
		trackVideoImpression(entry, observer, video) {
			const { target } = entry;
			const { isVideoImpressionTracked, timeoutId } = target;

			// Retrieve the mode and corresponding module name for metrics
			const { moduleName } = this.modes[this.mode === 'in-article' ? 'article' : this.mode].config;

			// Early return if video impression has already been tracked
			if (isVideoImpressionTracked) {
				// Stop observing the video if inview autoplay is not enabled
				if (!this.config.inviewAutoplay) {
					observer.unobserve(video);
					debug('[vueVideoPlayer] Unobserve Video:', video.id);
				}
				return;
			}

			// If the video is visible and no timeout exists, track the impression after 1000m
			if (entry.isIntersecting) {
				if (!timeoutId) {
					// eslint-disable-next-line no-param-reassign
					target.timeoutId = setTimeout(() => {
						const impression = `${moduleName}_${this.video.vidChron}`;

						// Trigger Mode Impression Metric Call and mark the video as tracked
						if (window.globalMetrics) {
							window.globalMetrics.setMetric('link.mviewable', impression);
						} else if (window.s_md) {
							window.s_md.contextData['wb.mviewable'] = impression;
						}
						window.wmdPageLink();
						target.isVideoImpressionTracked = true;
						debug('[vueVideoPlayer] Video Impression Tracked:', impression);
					}, 1000);
				}
			} else if (timeoutId) {
				// Clear timeout if the video is no longer visible
				clearTimeout(timeoutId);
				// eslint-disable-next-line no-param-reassign
				target.timeoutId = null;
			}
		},
	},
};
